/**
 * Standard pours: the measures a bar actually pours to.
 *
 * A bar does not measure 14 mL, or half a millilitre: it has jiggers, and a
 * drink is written in what they hold. The organisation keeps its own table of
 * those amounts (Organisation › Settings, on `ORG#<id> / META.standardPours`),
 * and the development chat may only propose a build poured in them.
 *
 * ## The defaults
 *
 * Above Board's own, counted from its recipes (the costing workbook's 57
 * Signature and Classics drinks, and the 89 in the older Airtable export):
 * these ten amounts are 93–95% of every line written in millilitres, and the
 * first six alone are about three quarters. Nothing is poured at 45 or 60;
 * the 7.5 is the sugar-syrup measure. A new organisation starts from the same
 * table and edits it.
 *
 * ## What the table governs
 *
 * Only lines poured by volume. The rest are counted, not measured, and stay
 * as they are written:
 *
 *   - **dashes and drops** — bitters, acid, absinthe — in whole numbers;
 *   - **a top** — soda to fill — which has no amount;
 *   - **counted items** — an egg white, a garnish;
 *   - **weighed lines**, in grams;
 *   - **dilution** — the water a shake or a stir adds — which nobody pours.
 *
 * An empty table switches the rule off: any amount is accepted, as before the
 * table existed.
 *
 * ## Batching
 *
 * A bar batches: the shelf-stable part of a drink — spirits, liqueurs,
 * vermouths, syrups, bitters, and usually not the citrus — is mixed ahead in
 * bulk and poured at service as one measure. That is what makes a drink with
 * many ingredients quick to make, and it changes what has to be a measure. A
 * batch is made by the litre, so the amounts inside it are free: 22 mL of one
 * thing and 13 of another per serve is fine, because nobody pours them one
 * serve at a time. What is poured at service is the batch, so the **total**
 * of a build's batched lines must be one of the bar's measures.
 *
 * A line marked `batched` is therefore not held to the table on its own; the
 * sum of the batched lines poured by volume is.
 */
export const DEFAULT_STANDARD_POURS = [5, 7.5, 10, 15, 20, 25, 30, 35, 40, 50].map((ml) => ({ ml }));
/** Enough for every jigger behind a bar, and short enough to read. */
export const MAX_STANDARD_POURS = 24;
/** Nothing smaller is a measure: under this it is a dash. */
export const MIN_POUR_ML = 1;
export const MAX_POUR_ML = 1000;
const tidyMl = (ml) => Math.round(ml * 100) / 100;
/** A body's table, checked: amounts in range, each once, smallest first. */
export function validateStandardPours(raw) {
    if (!Array.isArray(raw))
        return { ok: false, error: "standardPours must be a list" };
    if (raw.length > MAX_STANDARD_POURS)
        return { ok: false, error: `standardPours has more than ${MAX_STANDARD_POURS} rows` };
    const seen = new Map();
    for (const [i, row] of raw.entries()) {
        if (typeof row !== "object" || row === null)
            return { ok: false, error: `standardPours[${i}] must be an object` };
        const { ml } = row;
        if (typeof ml !== "number" || !Number.isFinite(ml) || ml < MIN_POUR_ML || ml > MAX_POUR_ML) {
            return { ok: false, error: `standardPours[${i}].ml must be between ${MIN_POUR_ML} and ${MAX_POUR_ML} mL` };
        }
        const amount = tidyMl(ml);
        if (seen.has(amount))
            return { ok: false, error: `standardPours lists ${amount} mL twice` };
        seen.set(amount, { ml: amount });
    }
    return { ok: true, value: [...seen.values()].sort((a, b) => a.ml - b.ml) };
}
/**
 * The table an organisation is working to: what is stored when it has saved
 * one (an empty list included, which is "no rule"), and the defaults when it
 * never has. Anything unreadable is treated as never saved.
 */
export function settledStandardPours(raw) {
    if (raw === undefined || raw === null)
        return DEFAULT_STANDARD_POURS;
    const checked = validateStandardPours(raw);
    return checked.ok ? checked.value : DEFAULT_STANDARD_POURS;
}
/** `5, 7.5, 10 and 15 mL`, for a sentence. */
export function poursText(pours) {
    const amounts = pours.map((p) => String(p.ml));
    if (amounts.length === 0)
        return "";
    const list = amounts.length === 1 ? amounts[0] : `${amounts.slice(0, -1).join(", ")} and ${amounts[amounts.length - 1]}`;
    return `${list} mL`;
}
/**
 * What a build's batch pours per serve: its batched lines measured by volume,
 * added up. Dashes inside a batch are left out, as they are too small to
 * move which jigger the batch is poured with. Zero when nothing is batched.
 */
export function batchPourMl(lines, ingredients) {
    const total = lines
        .filter((l) => l.batched && (l.unit ?? "ml") === "ml" && ingredients.get(l.ingredientId)?.kind !== "dilution")
        .reduce((sum, l) => sum + l.ml, 0);
    return tidyMl(total);
}
/** The standard pour nearest an amount; ties go to the smaller. */
export function nearestPour(ml, pours) {
    let best = null;
    for (const p of pours) {
        if (best === null || Math.abs(p.ml - ml) < Math.abs(best - ml))
            best = p.ml;
    }
    return best;
}
/**
 * What is wrong with a build's measures, one sentence per line that breaks
 * the rule, or an empty list when it is all pourable. See the module note
 * for what is and is not governed.
 */
export function pourProblems(lines, pours, ingredients) {
    const problems = [];
    for (const line of lines) {
        const ing = ingredients.get(line.ingredientId);
        const name = ing?.name ?? line.ingredientId;
        if (ing?.kind === "dilution")
            continue;
        const unit = line.unit ?? "ml";
        if (unit === "dash" || unit === "drop") {
            const count = line.qty ?? 1;
            if (!Number.isInteger(count) || count < 1) {
                problems.push(`${name}: ${count} ${unit} is not a whole number of ${unit === "dash" ? "dashes" : "drops"}.`);
            }
            continue;
        }
        if (unit !== "ml" || pours.length === 0)
            continue;
        // Inside the batch the ratios are free; the batch's pour is checked below.
        if (line.batched)
            continue;
        if (!pours.some((p) => Math.abs(p.ml - line.ml) < 0.005)) {
            const near = nearestPour(line.ml, pours);
            problems.push(`${name}: ${tidyMl(line.ml)} mL is not one of the bar's measures` +
                (line.ml < MIN_POUR_ML ? "; an amount that small is a dash, written with unit \"dash\"." : near !== null ? `; the nearest is ${near} mL.` : "."));
        }
    }
    const batch = batchPourMl(lines, ingredients);
    if (batch > 0 && pours.length > 0 && !pours.some((p) => Math.abs(p.ml - batch) < 0.005)) {
        const near = nearestPour(batch, pours);
        problems.push(`The batch pours ${batch} mL a serve, which is not one of the bar's measures` +
            (near !== null ? `; the nearest is ${near} mL. Change the amounts inside the batch so they add up to a measure.` : "."));
    }
    return problems;
}
//# sourceMappingURL=pours.js.map